import React from 'react'
import {
  Button,
  FormModalTitle,
  FormModalContent,
  FormModalActions,
} from 'sputnik-ui'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import { addThirdPartyIntercom } from 'features/Intercoms/intercomsSlice'
import { unwrapResult } from '@reduxjs/toolkit'
import { fetchAvailable } from 'features/Devices/installationsSlice'
import { Select, MenuItem, FormHelperText } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import InputWithError from 'components/atoms/InputWithError'
import RtspLinkExplain from 'components/atoms/RtspLinkExplain'
import 'utils/thirdPartyYup'
import {
  THIRD_PARTY_VENDORS,
  thirdPartyIntercomKinds,
  passwordSchema,
} from 'utils/thirdPartyIntercomKinds'

const styles = {
  input: {
    marginBottom: 5,
    marginTop: 5,
  },
}

const DEFAULT_VENDOR = 'beward'

const withCamera = (kind) => !!THIRD_PARTY_VENDORS[kind]?.hasCamera
const withHostAddress = (kind) => THIRD_PARTY_VENDORS[kind]?.address === 'host'

export default function AddThirdPartyIntercom({
  open,
  handleClose,
  onSuccess,
  ...props
}) {
  const dispatch = useDispatch()

  const { t } = useTranslation(['common', 'device', 'info'])
  const validationSchema = yup.object().shape({
    alias: yup.string().max(60),
    intercom_kind: yup
      .string()
      .oneOf(thirdPartyIntercomKinds)
      .required(t('common:field.error.required')),
    series: yup.string().required(t('common:field.error.required')),
    // У контроллера Eltex камеры нет: RTSP не спрашиваем, бэкенд подставит заглушку.
    rtsp_link: yup.string().when('intercom_kind', {
      is: withCamera,
      then: yup
        .string()
        .rtspLink(t('common:field.error.rtspLink'))
        .required(t('common:field.error.required')),
      otherwise: yup.string().notRequired(),
    }),
    // Beward — только IPv4:порт, Eltex — ещё и хост:порт.
    ip_address: yup.string().when('intercom_kind', {
      is: withHostAddress,
      then: yup
        .string()
        .hostSocket(t('common:field.error.wrong_host'))
        .required(t('common:field.error.required')),
      otherwise: yup
        .string()
        .ipv4Socket(t('common:field.error.wrong_ip'))
        .required(t('common:field.error.required')),
    }),
    login: yup.string().required(t('common:field.error.required')),
    // Минимальная длина пароля зависит от вендора: Beward — 6, Eltex — без ограничения.
    password: yup
      .string()
      .when('intercom_kind', (kind, schema) => passwordSchema(schema, kind, t)),
  })

  const formik = useFormik({
    validationSchema,
    initialValues: {
      rtsp_link: '',
      intercom_kind: DEFAULT_VENDOR,
      series: THIRD_PARTY_VENDORS[DEFAULT_VENDOR].series[0].value,
      ip_address: '',
      login: '',
      password: '',
      alias: '',
    },
    onSubmit: (val) => {
      const { rtsp_link, ...rest } = val
      const payload = withCamera(val.intercom_kind)
        ? { ...rest, rtsp_link }
        : rest
      dispatch(addThirdPartyIntercom(payload))
        .then(unwrapResult)
        .then((el) => {
          onSuccess(el?.serial_number)
          handleClose?.()
        })
        .then(() => dispatch(fetchAvailable({ q: '' })))
    },
  })

  const vendor = THIRD_PARTY_VENDORS[formik.values.intercom_kind]

  // Смена вендора не должна подсвечивать «Обязательное поле» под ещё не тронутыми полями:
  // значения меняем без валидации и сбрасываем ошибки предыдущего вендора (например, RTSP).
  const handleVendorChange = (e) => {
    const kind = e.target.value
    formik.setFieldValue('intercom_kind', kind, false)
    formik.setFieldValue(
      'series',
      THIRD_PARTY_VENDORS[kind].series[0].value,
      false
    )
    if (!withCamera(kind)) {
      formik.setFieldValue('rtsp_link', '', false)
    }
    formik.setErrors({})
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormModalTitle>
        {t('devices:intercoms.third_party.addition')}
      </FormModalTitle>
      <FormModalContent
        style={{ whiteSpace: 'pre-wrap' }}
        label={t(
          vendor.hasCamera
            ? 'devices:intercoms.third_party.info'
            : 'devices:intercoms.third_party.info_eltex'
        )}
      >
        <FormHelperText>
          {t('devices:intercoms.third_party.intercom_kind')}
        </FormHelperText>
        <Select
          required
          name="intercom_kind"
          fullWidth
          value={formik.values.intercom_kind}
          onChange={handleVendorChange}
          style={styles.input}
        >
          {thirdPartyIntercomKinds.map((kind) => (
            <MenuItem value={kind} key={kind}>
              {THIRD_PARTY_VENDORS[kind].label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {t('devices:intercoms.third_party.series')}
        </FormHelperText>
        <Select
          required
          name="series"
          fullWidth
          value={formik.values.series}
          onChange={(e) => formik.setFieldValue('series', e.target.value)}
          style={styles.input}
        >
          {vendor.series.map(({ value, label }) => (
            <MenuItem value={value} key={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        <InputWithError
          name="ip_address"
          formik={formik}
          label={t(
            vendor.address === 'host'
              ? 'devices:intercoms.third_party.address_host'
              : 'devices:intercoms.third_party.ip_address'
          )}
          required
        />
        <InputWithError
          name="login"
          formik={formik}
          label={t('devices:intercoms.third_party.login')}
          required
        />
        <InputWithError
          name="password"
          formik={formik}
          label={t('devices:intercoms.third_party.password')}
          required
        />

        {vendor.hasCamera && (
          <>
            <InputWithError
              name="rtsp_link"
              formik={formik}
              label={t('devices:cameras.third_party.rtsp')}
              required
              style={{
                marginTop: 5,
              }}
            />
            <RtspLinkExplain style={{ marginTop: 20 }} />
          </>
        )}
        <InputWithError
          name="alias"
          formik={formik}
          label={t('devices:cameras.third_party.alias')}
          required={false}
          style={{ marginBottom: 24 }}
        />
      </FormModalContent>
      <FormModalActions>
        <Button
          variant="v2"
          primary
          fullWidth
          type="submit"
          disabled={Object.values(formik.errors).length > 0}
        >
          {t('devices:intercoms.add')}
        </Button>
      </FormModalActions>
    </form>
  )
}
